import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Ticket, Trophy, Flag, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { ThemedShell, ThemedCard, ThemedProgress } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { MarbleRace } from "../../components/child/MarbleRace";
import { sfx } from "../../lib/sound";

const flagUrl = (code) => `https://flagcdn.com/w160/${code}.png`;

export default function RaceGame() {
  const navigate = useNavigate();
  const { terms } = useTheme();
  const { refresh } = useAuth();
  const [state, setState] = useState(null);
  const [phase, setPhase] = useState("lobby"); // lobby | pick | racing | result
  const [race, setRace] = useState(null);       // {race_id, marbles}
  const [pick, setPick] = useState(null);
  const [outcome, setOutcome] = useState(null);  // {winner, order, won, coins_earned, golden_flag}
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/child/race").then(({ data }) => setState(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const muted = { color: "var(--t-muted)" };

  const startRace = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/child/race/start");
      setRace(data); setPick(null); setOutcome(null); setPhase("pick");
      setState((s) => ({ ...s, tickets: data.tickets }));
      sfx.click();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const go = () => {
    if (pick == null) { toast.error("Kies eerst een knikker!"); return; }
    sfx.click();
    setPhase("racing");
  };

  const onRaceDone = async (winner) => {
    try {
      const { data } = await api.post("/child/race/finish", { race_id: race.race_id, pick, winner });
      setOutcome(data);
      if (data.won) sfx.win(); else sfx.coin();
      setPhase("result"); load(); refresh();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setPhase("lobby"); }
  };

  // ---------------- LOBBY ----------------
  const renderLobby = () => {
    const prog = state ? `${state.progress}/${state.ticket_every}` : "";
    const canRace = state?.tickets > 0;
    return (
      <main className="max-w-3xl mx-auto px-5 pt-8 pb-16">
        <div className="text-center mb-8">
          <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} className="text-6xl mb-2">🏎️</motion.div>
          <h1 className="font-heading text-3xl font-black themed-heading">Knikker-GP</h1>
          <p className="font-semibold" style={muted}>Kies de winnaar, kijk de race en win gouden knikkers! 🏁</p>
        </div>

        <ThemedCard className="p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="flex items-center gap-2 font-heading font-extrabold text-lg"><Ticket className="w-5 h-5" style={{ color: "var(--t-accent)" }} /> Race-tickets</span>
            <span className="font-heading text-2xl font-black themed-heading" data-testid="race-tickets">{state?.tickets ?? "…"}</span>
          </div>
          {state && state.tickets < 1 && (
            <div>
              <ThemedProgress value={(state.progress / state.ticket_every) * 100} />
              <p className="text-xs font-semibold mt-1.5" style={muted}>Nog {state.ticket_every - state.progress} oefening(en) tot je volgende ticket ({prog})</p>
            </div>
          )}
          <Button onClick={startRace} disabled={!canRace || busy} data-testid="start-race-btn"
            className="w-full h-14 rounded-2xl font-bold text-lg mt-4 border-0 disabled:opacity-50"
            style={{ background: canRace ? "var(--t-primary)" : "var(--t-surface2)", color: canRace ? "var(--t-on-primary)" : "var(--t-muted)" }}>
            {canRace ? <>Start een race! <ChevronRight className="w-5 h-5 ml-1" /></> : "Verdien eerst een ticket"}
          </Button>
        </ThemedCard>

        {/* Podiumkast */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-extrabold themed-heading flex items-center gap-2"><Trophy className="w-5 h-5" style={{ color: "var(--t-accent)" }} /> Podiumkast</h2>
          {state?.total_wins > 0 && <span className="text-sm font-bold" style={muted}>{state.total_wins} gewonnen</span>}
        </div>
        {state?.collection?.length ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="race-podium">
            {state.collection.map((c) => (
              <ThemedCard key={c.code} className="p-3 flex flex-col items-center text-center" data-testid={`trophy-${c.code}`}>
                <div className="relative w-16 h-16 rounded-full overflow-hidden border-4 mb-1.5" style={{ borderColor: "#fbbf24", boxShadow: "0 0 14px rgba(251,191,36,0.5)" }}>
                  <img src={flagUrl(c.code)} alt={c.name} className="w-full h-full object-cover" />
                </div>
                <p className="font-bold text-sm leading-tight">{c.name}</p>
                {c.count > 1 && <span className="text-xs font-bold" style={{ color: "var(--t-accent)" }}>×{c.count}</span>}
              </ThemedCard>
            ))}
          </div>
        ) : (
          <ThemedCard className="p-8 text-center"><Flag className="w-10 h-10 mx-auto mb-2" style={muted} /><p className="font-semibold" style={muted}>Nog geen gouden knikkers. Win een race om er een te verzamelen!</p></ThemedCard>
        )}
      </main>
    );
  };

  // ---------------- PICK ----------------
  const renderPick = () => (
    <main className="max-w-3xl mx-auto px-5 pt-8 pb-16">
      <div className="text-center mb-8">
        <h1 className="font-heading text-3xl font-black themed-heading">Wie wint er? 🤔</h1>
        <p className="font-semibold" style={muted}>Tik op de knikker waarvan jij denkt dat hij wint</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
        {race.marbles.map((m, i) => {
          const sel = pick === i;
          return (
            <button key={i} onClick={() => { sfx.select(); setPick(i); }} data-testid={`marble-pick-${i}`}
              className="p-4 rounded-3xl border-4 flex flex-col items-center gap-2 transition-all active:scale-95"
              style={sel ? { borderColor: "var(--t-accent)", background: "var(--t-surface2)" } : { borderColor: "var(--t-border)", background: "var(--t-surface)" }}>
              <div className="w-16 h-16 rounded-full overflow-hidden border-4" style={{ borderColor: m.color }}>
                <img src={flagUrl(m.code)} alt={m.name} className="w-full h-full object-cover" />
              </div>
              <span className="font-heading font-extrabold text-sm">{m.name}</span>
            </button>
          );
        })}
      </div>
      <Button onClick={go} disabled={busy} data-testid="race-go-btn" className="w-full h-14 rounded-2xl font-bold text-lg border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>
        {busy ? "Klaar voor de start..." : <>Start de race! 🏁</>}
      </Button>
    </main>
  );

  // ---------------- RACING ----------------
  const renderRacing = () => (
    <main className="max-w-4xl mx-auto px-3 pt-4 pb-10">
      <p className="text-center font-semibold mb-3" style={muted}>Tik op <b>BOOST</b> ⚡ om jouw knikker sneller te maken en <b>VERTRAAG</b> ❄️ om de rest af te remmen!</p>
      <MarbleRace marbles={race.marbles} pickIndex={pick} onDone={onRaceDone} />
    </main>
  );

  // ---------------- RESULT ----------------
  const renderResult = () => {
    const winnerMarble = race.marbles[outcome.winner];
    return (
      <main className="max-w-xl mx-auto px-5 pt-12 pb-16">
        <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <ThemedCard className="p-8 text-center">
            <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 mb-3" style={{ borderColor: "#fbbf24", boxShadow: "0 0 20px rgba(251,191,36,0.6)" }}>
              <img src={flagUrl(winnerMarble.code)} alt={winnerMarble.name} className="w-full h-full object-cover" />
            </div>
            <p className="font-semibold" style={muted}>Winnaar:</p>
            <h1 className="font-heading text-3xl font-black themed-heading">{winnerMarble.name} 🏆</h1>
            <div className="mt-5 p-4 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="race-result">
              {outcome.won ? (
                <>
                  <p className="font-heading text-xl font-extrabold" style={{ color: "var(--t-success)" }}>Gewonnen! 🎉</p>
                  <p className="font-semibold mt-1" style={muted}>Je voorspelling klopte! Je krijgt een gouden knikker.</p>
                </>
              ) : (
                <p className="font-heading text-lg font-extrabold">Helaas, jouw knikker won niet 😅</p>
              )}
              <div className="flex items-center justify-center gap-1.5 mt-3 font-heading font-extrabold text-lg"><span className="text-xl">{terms.coinIcon}</span> +{outcome.coins_earned} {terms.coins}</div>
            </div>
            <div className="flex gap-3 mt-7">
              <Button onClick={() => setPhase("lobby")} className="flex-1 h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="race-back-lobby-btn">
                {state?.tickets > 0 ? "Nog een race!" : "Terug"}
              </Button>
            </div>
          </ThemedCard>
        </motion.div>
      </main>
    );
  };

  return (
    <ThemedShell>
      <header className="sticky top-0 z-30 border-b backdrop-blur-md" style={{ background: `color-mix(in srgb, var(--t-surface) 75%, transparent)`, borderColor: "var(--t-border)" }}>
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/leerling")} className="rounded-full font-bold" style={{ color: "var(--t-text)" }} data-testid="race-exit-btn"><ArrowLeft className="w-5 h-5 mr-1" /> {terms.dashboard}</Button>
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-sm" style={{ background: "var(--t-surface2)" }}><Ticket className="w-4 h-4" style={{ color: "var(--t-accent)" }} /> {state?.tickets ?? 0}</span>
        </div>
      </header>
      <AnimatePresence mode="wait">
        <motion.div key={phase} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          {phase === "lobby" && renderLobby()}
          {phase === "pick" && race && renderPick()}
          {phase === "racing" && race && renderRacing()}
          {phase === "result" && race && outcome && renderResult()}
        </motion.div>
      </AnimatePresence>
    </ThemedShell>
  );
}
